import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';

// The app's confirm dialog. Anything that moves money, closes a period or deletes data asks here,
// in words: `confirm(message, { title, danger, confirmLabel })` — e.g. title 'Approve 19 timesheets?',
// confirmLabel 'Approve 19'. The confirm button repeats the action instead of a generic 'Confirm'.
// Escape cancels; focus starts on the safe choice (Cancel) for dangerous actions.

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((message, { title = 'Are you sure?', danger = false, confirmLabel, cancelLabel = 'Cancel' } = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ message, title, danger, confirmLabel: confirmLabel || 'Confirm', cancelLabel });
    });
  }, []);

  const handleChoice = useCallback((result) => {
    setState(null);
    resolveRef.current?.(result);
  }, []);

  const cancelRef = useRef(null);
  const okRef = useRef(null);
  useEffect(() => {
    if (!state) return undefined;
    const back = document.activeElement;
    const t = setTimeout(() => (state.danger ? cancelRef.current : okRef.current)?.focus(), 0);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); handleChoice(false); }
      if (e.key === 'Tab') {
        const a = cancelRef.current; const b = okRef.current;
        if (!a || !b) return;
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); b.focus(); }
        else if (!e.shiftKey && document.activeElement === b) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey, true); back?.focus?.({ preventScroll: true }); };
  }, [state, handleChoice]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => handleChoice(false)} aria-hidden="true" />
          <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-msg"
            className="relative bg-white rounded-2xl shadow-modal w-full max-w-md p-6 animate-fade-up motion-reduce:animate-none">
            <div className="flex items-start gap-3 mb-5">
              <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${state.danger ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                <AlertTriangle className="w-[18px] h-[18px]" />
              </span>
              <div className="min-w-0">
                <h3 id="confirm-title" className="font-semibold text-slate-900 text-[17px] leading-snug">{state.title}</h3>
                <div id="confirm-msg" className="text-slate-600 mt-1.5 text-sm leading-relaxed whitespace-pre-line">{state.message}</div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button ref={cancelRef} type="button" onClick={() => handleChoice(false)} className="btn-secondary">
                {state.cancelLabel}
              </button>
              <button ref={okRef} type="button" onClick={() => handleChoice(true)} className={state.danger ? 'btn-danger' : 'btn-primary'}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within ConfirmProvider');
  return confirm;
}
